import type {
  AdminBulkResponse,
  AdminLicenseCreateRequest,
  AdminLicenseBulkAction,
  AdminLicenseDto,
  AdminLicensePatchRequest,
  AdminLicenseStatus,
} from '../types';
import { LICENSE_TYPE_LABELS } from '../types';

export const CREATABLE_LICENSE_TYPES = [
  { value: 0, label: 'Standard' },
  { value: 1, label: 'Floating' },
  { value: 2, label: 'Demo' },
  { value: 6, label: 'Test' },
] as const;

const CreatableTypeValues = new Set<number>(
  CREATABLE_LICENSE_TYPES.map((option) => option.value)
);
const LicenseStatuses = new Set<AdminLicenseStatus>(['active', 'suspended', 'expired']);
const SortColumnIds = new Set([
  'activationKey',
  'company',
  'type',
  'instances',
  'registrations',
  'exportElements',
  'status',
  'expires',
]);
const SortQueryToColumn: Record<string, string> = { dueTime: 'expires' };
const SortColumnToQuery: Record<string, string> = { expires: 'dueTime' };

export interface LicenseTableFilters {
  [key: string]: string;
  activationKey: string;
  company: string;
  type: string;
  status: string;
}

export interface LicenseTableQueryState {
  filters: LicenseTableFilters;
  sort: {
    columnId: string;
    direction: 'ascending' | 'descending';
  } | null;
}

export interface LicenseDraft {
  companyId: number;
  type: number;
  allowedInstances: number;
  allowedRegistrations: number;
  remainingExportElements: number | null;
  dueDate: string;
  status: AdminLicenseStatus;
  notes: string;
}

export type LicenseDraftErrors = Partial<Record<keyof LicenseDraft, string>>;

const toUtcDateInput = (value: string | null): string => {
  if (!value) {
    return '';
  }
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return '';
  }
  return new Date(timestamp).toISOString().slice(0, 10);
};

const isValidDateInput = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return false;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
};

export const getLicenseTypeLabel = (type: number): string =>
  LICENSE_TYPE_LABELS[type] ?? `Type ${type}`;

export const getLicenseOccupancyRatio = (license: AdminLicenseDto): number =>
  Number.isFinite(license.allowedInstances) && license.allowedInstances > 0
    ? license.machinesCount / license.allowedInstances
    : 0;

/** Allowance a license gets when it is switched to Demo without an explicit
 *  value — mirrors the server-side `DemoExportQuotaPolicy` default so the
 *  drawer shows what the API would seed anyway. */
export const DEMO_EXPORT_ELEMENT_QUOTA = 1000;

/** Table cell for the element allowance: `Unlimited` when the license carries
 *  no limit, otherwise the bare remaining element count. */
export const formatExportElementQuota = (remaining: number | null): string =>
  remaining === null ? 'Unlimited' : `${remaining}`;

export const getLicenseExpirySortValue = (license: AdminLicenseDto): number => {
  if (!license.dueTime) {
    return Number.POSITIVE_INFINITY;
  }
  const timestamp = Date.parse(license.dueTime);
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
};

export const parseLicenseTableQuery = (query: URLSearchParams): LicenseTableQueryState => {
  const type = query.get('type') ?? '';
  const status = query.get('status') ?? '';
  const rawSort = query.get('sort') ?? '';
  const descending = rawSort.startsWith('-');
  const querySortId = descending ? rawSort.slice(1) : rawSort;
  const sortColumnId = SortQueryToColumn[querySortId] ?? querySortId;
  const numericType = Number(type);

  return {
    filters: {
      activationKey: query.get('key') ?? '',
      company: query.get('company') ?? '',
      type: type !== ''
        && Object.prototype.hasOwnProperty.call(LICENSE_TYPE_LABELS, numericType)
        ? type
        : '',
      status: LicenseStatuses.has(status as AdminLicenseStatus) ? status : '',
    },
    sort: SortColumnIds.has(sortColumnId)
      ? { columnId: sortColumnId, direction: descending ? 'descending' : 'ascending' }
      : null,
  };
};

export const serializeLicenseTableQuery = (
  state: LicenseTableQueryState
): URLSearchParams => {
  const query = new URLSearchParams();
  const entries = [
    ['key', state.filters.activationKey],
    ['company', state.filters.company],
    ['type', state.filters.type],
    ['status', state.filters.status],
  ] as const;
  entries.forEach(([key, value]) => {
    if (value !== '') {
      query.set(key, value);
    }
  });
  if (state.sort) {
    const prefix = state.sort.direction === 'descending' ? '-' : '';
    const queryColumn = SortColumnToQuery[state.sort.columnId] ?? state.sort.columnId;
    query.set('sort', `${prefix}${queryColumn}`);
  }
  return query;
};

export const createLicenseDraft = (license: AdminLicenseDto): LicenseDraft => ({
  companyId: license.companyId ?? 0,
  type: license.type,
  allowedInstances: license.allowedInstances,
  allowedRegistrations: license.allowedRegistrations,
  remainingExportElements: license.remainingExportElements,
  dueDate: toUtcDateInput(license.dueTime),
  status: license.status,
  notes: license.notes ?? '',
});

export const isLicenseDraftDirty = (
  draft: LicenseDraft,
  original: LicenseDraft,
  pendingRevokeIds: readonly number[]
): boolean => JSON.stringify(draft) !== JSON.stringify(original) || pendingRevokeIds.length > 0;

export const togglePendingMachineRevoke = (
  pendingIds: readonly number[],
  machineId: number
): number[] => pendingIds.includes(machineId)
  ? pendingIds.filter((id) => id !== machineId)
  : [...pendingIds, machineId];

export const addLicenseCalendarYear = (value: string): string => {
  if (!isValidDateInput(value)) {
    return value;
  }
  const [year, month, day] = value.split('-').map(Number);
  const targetYear = year + 1;
  const lastDay = new Date(Date.UTC(targetYear, month, 0)).getUTCDate();
  return [targetYear, String(month).padStart(2, '0'), String(Math.min(day, lastDay)).padStart(2, '0')]
    .join('-');
};

export const createNewLicenseDraft = (
  companyId: number,
  now: Date = new Date()
): LicenseDraft => ({
  companyId,
  type: 0,
  allowedInstances: 5,
  allowedRegistrations: 10,
  remainingExportElements: null,
  dueDate: addLicenseCalendarYear(now.toISOString().slice(0, 10)),
  status: 'active',
  notes: '',
});

export const validateLicenseDraft = (draft: LicenseDraft): LicenseDraftErrors => {
  const errors: LicenseDraftErrors = {};
  if (!Number.isInteger(draft.companyId) || draft.companyId <= 0) {
    errors.companyId = 'Choose a company.';
  }
  if (!CreatableTypeValues.has(draft.type)) {
    errors.type = 'Choose Standard, Floating, Demo, or Test.';
  }
  if (!Number.isInteger(draft.allowedInstances) || draft.allowedInstances < 1) {
    errors.allowedInstances = 'Allowed instances must be at least 1.';
  }
  if (!Number.isInteger(draft.allowedRegistrations) || draft.allowedRegistrations < 1) {
    errors.allowedRegistrations = 'Registration limit must be at least 1.';
  }
  if (draft.remainingExportElements !== null
      && (!Number.isInteger(draft.remainingExportElements) || draft.remainingExportElements < 0)) {
    errors.remainingExportElements = 'Export elements remaining must be a whole number of 0 or more.';
  }
  if (!isValidDateInput(draft.dueDate)) {
    errors.dueDate = 'Enter a valid expiry date.';
  }
  if (draft.notes.length > 10_000) {
    errors.notes = 'Notes must be 10,000 characters or fewer.';
  }
  return errors;
};

const dateInputToUtc = (value: string): string => `${value}T00:00:00.000Z`;

export const createLicenseCreateRequest = (
  draft: LicenseDraft
): AdminLicenseCreateRequest => ({
  companyId: draft.companyId,
  type: draft.type,
  allowedInstances: draft.allowedInstances,
  allowedRegistrations: draft.allowedRegistrations,
  remainingExportElements: draft.remainingExportElements,
  dueTime: dateInputToUtc(draft.dueDate),
  status: draft.status,
  notes: draft.notes,
});

export const createLicensePatchRequest = (
  draft: LicenseDraft,
  original: LicenseDraft
): AdminLicensePatchRequest | null => {
  const patch: AdminLicensePatchRequest = {};
  if (draft.companyId !== original.companyId) {
    patch.companyId = draft.companyId;
  }
  if (draft.type !== original.type) {
    patch.type = draft.type;
  }
  if (draft.allowedInstances !== original.allowedInstances) {
    patch.allowedInstances = draft.allowedInstances;
  }
  if (draft.allowedRegistrations !== original.allowedRegistrations) {
    patch.allowedRegistrations = draft.allowedRegistrations;
  }
  if (draft.remainingExportElements !== original.remainingExportElements) {
    patch.remainingExportElements = draft.remainingExportElements;
  }
  if (draft.dueDate !== original.dueDate) {
    patch.dueTime = dateInputToUtc(draft.dueDate);
  }
  if (draft.status !== original.status) {
    patch.status = draft.status;
  }
  if (draft.notes !== original.notes) {
    patch.notes = draft.notes;
  }
  return Object.keys(patch).length > 0 ? patch : null;
};

export const reduceLicenseSelectionAfterBulk = (
  selectedIds: readonly number[],
  result: AdminBulkResponse
): number[] => {
  const succeeded = new Set(result.succeeded);
  return selectedIds.filter((id) => !succeeded.has(id));
};

const BulkPastTense: Record<AdminLicenseBulkAction, string> = {
  extend: 'Extended',
  suspend: 'Suspended',
  delete: 'Deleted',
};

const licenseCount = (count: number): string =>
  `${count} ${count === 1 ? 'license' : 'licenses'}`;

export const formatLicenseBulkResult = (
  action: AdminLicenseBulkAction,
  result: AdminBulkResponse
): string => {
  const succeeded = `${BulkPastTense[action]} ${licenseCount(result.succeeded.length)}`;
  if (result.failed.length === 0) {
    return `${succeeded}.`;
  }
  const codes = [...new Set(result.failed.map((failure) => failure.code))].join(', ');
  return `${succeeded}; ${result.failed.length} failed (${codes}).`;
};
